/**
 * The rules for a template version's environment variables, in one place.
 *
 * The publish wizard edits them; the member workbench and System Admin's review
 * panel read them. The rules mirror `api/src/services/runtimeEnvironment.ts`,
 * which refuses the same things with a single sentence — checking here first is
 * what puts every problem under its own field at once (DESIGN_SYSTEM §5
 * Validation). Change one side, change the other.
 */

import type { EnvironmentVariable, TemplateProcessor, TemplateProcessorVersion } from '../types/api';

/** Mirror the API's bounds. */
export const ENVIRONMENT_MAX_VARIABLES = 100;
export const ENVIRONMENT_NAME_MAX_LENGTH = 128;
export const ENVIRONMENT_VALUE_MAX_LENGTH = 8192;

/** A C identifier, as the API requires. */
const NAME_RE = /^[A-Za-z_][A-Za-z0-9_]*$/;

/** The names the platform sets on every processor. Mirrors `RESERVED_NAMES` in the API. */
const RESERVED_NAMES = new Set([
  'PROCESSOR_ID',
  'DORY_PROCESSOR_ID',
  'DORY_STATE_TOKEN',
  'DORY_APP_VERSION',
  'DORY_SENSOR_ID',
  'DORY_SENSOR_TYPE',
  'DORY_SENSOR_CONNECTION_CONFIG',
  'DORY_SENSOR_METADATA',
  'DORY_GEO_REFERENCE_POINTS',
  'DORY_GEOHASH',
  'ORCHESTRATOR_URL',
  'WORKLOAD_TYPE',
  'POD_NAME',
  'POD_NAMESPACE',
  'POD_IP',
  'NODE_NAME',
  'OTEL_SERVICE_NAME',
  'OTEL_RESOURCE_ATTRIBUTES',
  'OTEL_EXPORTER_OTLP_ENDPOINT',
]);
/** Mirrors `RESERVED_PREFIXES` in the API. */
const RESERVED_PREFIXES = ['DORY_RABBITMQ_', 'KUBERNETES_'];

/**
 * What the field says about secrets. Its truth is set by the API: a secret is
 * left out of every read, and it is still stored and delivered as an ordinary
 * environment variable (`services/runtimeEnvironment.ts`). Change them together.
 */
export const ENVIRONMENT_HELP =
  'Settings your container reads when it starts, such as a storage bucket or an API key. A version keeps them for good: to change one, submit a new version.';
export const ENVIRONMENT_SECRET_HELP =
  'A secret is never shown again after you submit, not even to reviewers. It is still stored as plain text and handed to the container as an ordinary environment variable.';

/** One editable variable. */
export interface EnvironmentRow {
  /** Stable React key. Never sent. */
  rowId: string;
  name: string;
  value: string;
  secret: boolean;
  /**
   * The name of the saved secret this row was loaded as, or null. The API never
   * sent that value, so while the row still carries this name an empty value
   * means "keep what is saved". A renamed row keeps nothing: the saved value
   * belongs to the old name, and sending it under a new one would hand the
   * container a value its author never put there.
   */
  savedSecretName: string | null;
}

export type EnvironmentField = 'name' | 'value';
export type EnvironmentRowErrors = Record<string, Partial<Record<EnvironmentField, string>>>;

export interface EnvironmentErrors {
  rows: EnvironmentRowErrors;
  /** A problem with the list as a whole. */
  list?: string;
}

/** What changed, so exactly the errors it made stale are cleared. */
export type EnvironmentChange = { rowId: string; field: EnvironmentField } | { list: true };

let rowCounter = 0;
const nextRowId = (): string => {
  rowCounter += 1;
  return `environment-row-${rowCounter}`;
};

export const newEnvironmentRow = (): EnvironmentRow => ({
  rowId: nextRowId(),
  name: '',
  value: '',
  secret: false,
  savedSecretName: null,
});

/**
 * The saved version a wizard's rows load from, which is also the version the API
 * reads kept secrets from. A new version starts from the LIVE one, like its event
 * types. Setup starts from the LATEST — the one the API would inherit from —
 * because setup can resume on a template that already has versions, and loading
 * nothing there would submit an empty list and wipe its variables.
 */
export function environmentSourceFor(
  template: Pick<TemplateProcessor, 'latestVersion' | 'selectedVersion'> | null,
  mode: 'setup' | 'new-version',
): TemplateProcessorVersion | null {
  if (!template) return null;
  return mode === 'new-version'
    ? (template.selectedVersion ?? template.latestVersion ?? null)
    : (template.latestVersion ?? template.selectedVersion ?? null);
}

/**
 * The editable rows for a saved version's variables.
 *
 * Kubernetes Secret references are left out: they are not editable here, and the
 * API carries them into the next version on its own.
 */
export function environmentRowsFrom(variables: EnvironmentVariable[] | undefined): EnvironmentRow[] {
  return (variables ?? [])
    .filter((variable) => variable.source === 'template')
    .map((variable) => ({
      rowId: nextRowId(),
      name: variable.name,
      value: variable.secret ? '' : (variable.value ?? ''),
      secret: variable.secret,
      savedSecretName: variable.secret ? variable.name : null,
    }));
}

/** Whether the row still carries the name of the saved secret it was loaded as. */
export const isSavedSecret = (row: EnvironmentRow): boolean =>
  row.savedSecretName !== null && row.name === row.savedSecretName;

function isReserved(name: string): boolean {
  return RESERVED_NAMES.has(name) || RESERVED_PREFIXES.some((prefix) => name.startsWith(prefix));
}

/** Every problem at once, per row and per field. Empty `rows` and no `list` means valid. */
export function validateEnvironmentRows(
  rows: EnvironmentRow[],
  options: { kubernetesSecretNames?: string[] } = {},
): EnvironmentErrors {
  const errors: EnvironmentErrors = { rows: {} };
  if (rows.length > ENVIRONMENT_MAX_VARIABLES) {
    errors.list = `A version can have at most ${ENVIRONMENT_MAX_VARIABLES} environment variables.`;
  }

  const fromKubernetes = new Set(options.kubernetesSecretNames ?? []);
  const seen = new Set<string>();
  for (const row of rows) {
    const rowErrors: Partial<Record<EnvironmentField, string>> = {};
    // Not trimmed: the container receives the name exactly as written.
    if (row.name === '') rowErrors.name = 'Enter a name, or remove this variable.';
    else if (row.name.length > ENVIRONMENT_NAME_MAX_LENGTH) {
      rowErrors.name = `Names must be ${ENVIRONMENT_NAME_MAX_LENGTH} characters or fewer.`;
    } else if (!NAME_RE.test(row.name)) {
      rowErrors.name = "Use letters, digits and underscores, and don't start with a digit.";
    } else if (isReserved(row.name)) {
      rowErrors.name = 'The platform sets this variable for every processor.';
    } else if (fromKubernetes.has(row.name)) {
      rowErrors.name = 'This name already comes from a Kubernetes Secret.';
    } else if (seen.has(row.name)) {
      rowErrors.name = 'Another variable already uses this name.';
    }
    if (row.name !== '') seen.add(row.name);

    if (row.value.length > ENVIRONMENT_VALUE_MAX_LENGTH) {
      rowErrors.value = `Values must be ${ENVIRONMENT_VALUE_MAX_LENGTH} characters or fewer.`;
    } else if (row.value.includes('\u0000')) {
      rowErrors.value = "Values can't contain a null character.";
    } else if (row.value === '' && row.savedSecretName !== null) {
      // This browser never had the saved value, so it can neither move it to
      // another name nor show it in the clear.
      if (!isSavedSecret(row)) rowErrors.value = "Enter a value. A renamed secret doesn't keep its saved value.";
      else if (!row.secret) rowErrors.value = 'Type the value again to stop keeping it secret.';
    }

    if (rowErrors.name || rowErrors.value) errors.rows[row.rowId] = rowErrors;
  }
  return errors;
}

/**
 * The errors after a change: only the changed field's error clears
 * (DESIGN_SYSTEM §5 Validation), and the whole-list error only when a row is
 * added or removed.
 */
export function clearEnvironmentError(errors: EnvironmentErrors, change: EnvironmentChange): EnvironmentErrors {
  if ('list' in change) return errors.list ? { rows: errors.rows } : errors;
  const current = errors.rows[change.rowId];
  if (!current?.[change.field]) return errors;
  const { [change.field]: _stale, ...remaining } = current;
  const rows = { ...errors.rows };
  if (Object.keys(remaining).length > 0) rows[change.rowId] = remaining;
  else delete rows[change.rowId];
  return { ...errors, rows };
}

/**
 * The errors after an edit, for the field's `onChange`: the changed field's own
 * error clears (it is not re-checked until submit), and so does any error the
 * edit made stale elsewhere — renaming one of two FOO rows, or removing it,
 * fixes the other row's duplicate (DESIGN_SYSTEM §5 Validation). An error only
 * ever clears here; a new one waits for submit.
 */
export function reconcileEnvironmentErrors(
  errors: EnvironmentErrors,
  rows: EnvironmentRow[],
  change: EnvironmentChange,
  options: { kubernetesSecretNames?: string[] } = {},
): EnvironmentErrors {
  const cleared = clearEnvironmentError(errors, change);
  if (Object.keys(cleared.rows).length === 0) return cleared;
  const current = validateEnvironmentRows(rows, options).rows;
  const stillHeld: EnvironmentRowErrors = {};
  for (const [rowId, fields] of Object.entries(cleared.rows)) {
    const kept: Partial<Record<EnvironmentField, string>> = {};
    if (fields.name && current[rowId]?.name) kept.name = fields.name;
    if (fields.value && current[rowId]?.value) kept.value = fields.value;
    if (kept.name || kept.value) stillHeld[rowId] = kept;
  }
  return { ...cleared, rows: stillHeld };
}

export const hasEnvironmentErrors = (errors: EnvironmentErrors): boolean =>
  Boolean(errors.list) || Object.keys(errors.rows).length > 0;

/**
 * The request body's `environment`. A saved secret still under its own name with
 * nothing typed goes without a value, which the API reads as "keep the saved one".
 */
export function toEnvironmentPayload(
  rows: EnvironmentRow[],
): Array<{ name: string; value?: string; secret: boolean }> {
  return rows.map((row) =>
    isSavedSecret(row) && row.secret && row.value === ''
      ? { name: row.name, secret: row.secret }
      : { name: row.name, value: row.value, secret: row.secret },
  );
}

/** Whether two lists would submit the same thing. Row ids are not compared. */
export function sameEnvironment(a: EnvironmentRow[], b: EnvironmentRow[]): boolean {
  return (
    a.length === b.length &&
    a.every(
      (row, index) =>
        row.name === b[index].name &&
        row.value === b[index].value &&
        row.secret === b[index].secret &&
        row.savedSecretName === b[index].savedSecretName,
    )
  );
}
